
import LogoutBtn from '@/components/auth/LogoutBtn.vue';
import Avatar from '@/components/UI/avatar/Avatar.vue';
import store from '@/store';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import { format, utcToZonedTime } from 'date-fns-tz';
import { timeZoneDefault } from '@/utils/PortalConstants';
import NotificationsPanel from '@/components/notificationsPanel/NotificationsPanel.vue';
import { User } from '@/models/main';

@Component({
  components: {
    LogoutBtn,
    Avatar,
    NotificationsPanel,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
    ...mapState('moduleNotifications', { notifications: 'notifications' }),
    ...mapGetters('moduleQuantCart', ['cartCount', 'totalCartCount']),
  },
})
export default class SideMenu extends Vue {
  private drawer = true;
  private user!: User;
  public notifications;
  private cartCount!: number;
  private totalCartCount!: number;
  private miniVariant = true;
  private odooPath = `${process.env.VUE_APP_URL}/web`;
  private portalVersion = process.env.VERSION;
  private newVersionUrl = process.env.VUE_APP_NEW_VERSION_URL;
  private account: Array<Record<string, unknown>> = [
    { title: 'Profile', icon: 'mdi-account', path: '/profile' },
    { title: 'Address Book', icon: 'mdi-book-open-page-variant', path: '/user/address-book' },
    { title: 'Suburbs', icon: 'mdi-map-search', path: '/suburbs' },
    { title: 'Quant Cart', icon: 'mdi-cart-outline', path: '/cart' },
    { title: 'Technicians Catalog', icon: 'mdi-format-list-bulleted-type', path: '/technicians/catalog' },
    { title: 'Settings', icon: 'mdi-cog-outline' },
  ];
  private menuItems: Array<Record<string, unknown>> = [
    { title: 'Home', icon: 'mdi-home', path: '/' },
    { title: 'Dashboard', icon: 'mdi-chart-box-outline', path: '/dash' },
    { title: 'Job tickets', icon: 'mdi-mailbox', path: '/tickets' },
    { title: 'TV', icon: 'mdi-cast-connected', path: '/dash-tv' },
    { title: 'BFIX Dash', icon: 'mdi-cards', path: '/dash-bfix', staff: true },
    {
      title: 'Job Booking',
      icon: 'mdi-clipboard-text-outline',
      path: '/jobbooking',
    },
    { title: 'Search', icon: 'mdi-magnify', path: '/search' },
    { title: 'Search Postcode', icon: 'mdi-email-search-outline', path: '/search-postcode', staff: true },
    { title: 'Locations', icon: 'mdi-map-marker-multiple-outline', path: '/locations', staff: true },
    { title: 'Scheduler', icon: 'mdi-calendar-clock', path: '/scheduler', staff: true },
    {
      title: 'Quotes',
      icon: 'mdi-calculator',
      list: [
        { title: 'Quote Calculator', icon: 'mdi-calculator', path: '/quotes/new' },
        { title: 'My Quotes', icon: 'mdi-format-list-bulleted', path: '/quotes/list' },
      ],
    },
    {
      title: 'Reports',
      icon: 'mdi-poll',
      list: [
        { title: 'ETA Report', icon: 'mdi-calendar-question', path: '/reports/eta' },
        { title: 'KPI Report', icon: 'mdi-poll', path: '/reports/kpi', staff: true },
        { title: 'Driver Report', icon: 'mdi-truck', path: '/reports/driver', staff: true },
        { title: 'Tomorrow Report', icon: 'mdi-truck', path: '/reports/tomorrow', staff: true },
        { title: 'Run Sheet', icon: 'mdi-truck', path: '/reports/runsheet', staff: true },
        { title: 'Weekly Report', icon: 'mdi-calendar-question', path: '/reports/weekly-stats', staff: true },
        { title: 'AM/PM Jobs', icon: 'mdi-timetable', path: '/reports/am-pm-job', staff: true },
        { title: 'Odoo transfers', icon: 'mdi-truck', path: '/reports/transfers', staff: true },
        { title: 'Download PODs Bulk', icon: 'mdi-folder-download', path: '/reports/reports', staff: true },
        {
          title: 'Uninvoiced Jobs',
          icon: 'mdi-file-document-outline',
          path: '/reports/uninvoiced-jobs',
          staff: true,
        },
        {
          title: 'Outgoing Machines',
          icon: 'mdi-printer-outline',
          path: '/reports/outgoing-machines',
        },
        { title: 'Next Day Stops', icon: 'mdi-map-marker-multiple-outline', path: '/next-day-stops', staff: true },
        {
          title: 'Driver Retransmit',
          icon: 'mdi-cellphone-arrow-down',
          path: '/driver-retransmit',
          staff: true,
        },
      ],
    },
  ];

  // Dates
  private format = format;
  private utcToZonedTime = utcToZonedTime;
  private timeZoneDefault = timeZoneDefault;

  private loading = false;

  private miniVariantClick() {
    this.miniVariant = false;
  }

  // Helper function to calculate if we need to show menu item for user or not
  private showItemToNonStaff(item) {
    return Object.prototype.hasOwnProperty.call(item, 'staff')
      ? item.staff === false || (item.staff === true && this.user.staff)
      : true;
  }

  @Watch('miniVariant', { immediate: true, deep: true })
  private watchToMiniVariant(): void {
    store.dispatch('setSideMenuOpen', !this.miniVariant);
  }

  private mounted(): void {
    this.$store.dispatch('moduleQuantCart/loadFromStorage');

    // Open sidebar hotkey
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      const target = event.target as HTMLInputElement;
      if (event.code === 'BracketLeft' && target.nodeName === 'BODY') {
        this.miniVariant = !this.miniVariant;
      }
    });
  }

  private destroyed(): void {
    window.removeEventListener('keydown', () => null);
  }
}
